import React, {
  useEffect,
  useReducer,
  useState,
  useCallback,
  useRef,
} from 'react';
import api from '../api';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getError } from '../utils';
import {
  calculateLayoutWidth,
  calculateLayoutHeight,
} from '../utils/layoutGeometry';
import { assignLegendsToLayout } from '../utils/legendAssignment';
import { defaultIsActiveStates as defaultTextIsActiveStates } from '../components/customizer/shared/customizerScreen/textEntryDefaults';
import { defaultIsActiveStates as defaultIconIsActiveStates } from '../components/customizer/shared/customizerScreen/iconEntryDefaults';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import Button from 'react-bootstrap/Button';
import Editor from '@monaco-editor/react';
import KeycapBaseLayer from '../components/customizer/shared/customizerScreen/KeycapBaseLayer';
import StaticKeycapShadows from '../components/customizer/shared/customizerScreen/StaticKeycapShadows';
import KeycapOverlay from '../components/customizer/shared/customizerScreen/KeycapOverlay';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { fetchFont } from '../utils/fontHelpers';
import { addSelectedOptions } from '../utils/formInputs';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };

    case 'UPDATE_REQUEST':
      return { ...state, loadingUpdate: true };
    case 'UPDATE_SUCCESS':
      return { ...state, loadingUpdate: false };
    case 'UPDATE_FAIL':
      return { ...state, loadingUpdate: false };

    default:
      return state;
  }
};

// The entries the preview merges each legend over (utils/legendAssignment.js): the default font and
// the active states of a text, the active states of an icon. Static, so declared outside the component.
// A legend text needs its position and font size: this default has none (the customizer's fills them
// in), so a legend typed without them blanks the preview until they are added. Left as is (Oct 2026).
const defaultTextObject = {
  fontFamilyName: 'notosans',
  style: {
    fontFamily: 'NotoSans[wdth,wght]',
  },
  isActiveStates: defaultTextIsActiveStates,
  visible: true,
};

const defaultIconObject = {
  isActiveStates: defaultIconIsActiveStates,
  visible: true,
};

export default function LegendsEditScreen() {
  const navigate = useNavigate();
  const params = useParams();
  const { id: legendsId } = params; // deconstracts the id out of the params variable and its name is set to legendsId

  const [{ loading, error, loadingUpdate }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
  });

  const [fonts, setFonts] = useState({}); // store loaded fontkit font objects

  let totalVerticalSpacing = 0; // initialization of the totalVerticalSpacing variable. This variable is being used to accumulate the vertical spacing that comes with every row of keycaps and is needed to render the rows of keycaps at the correct height

  const [name, setName] = useState('');
  const [supportedLayouts, setSupportedLayouts] = useState([]);
  const [isActive, setIsActive] = useState(false);
  const [legends, setLegends] = useState([]);
  const [keycapData, setKeycapData] = useState([]);
  const [readOnly, setReadOnly] = useState(true);
  const [layoutWidth, setLayoutWidth] = useState(0);
  const [layoutHeight, setLayoutHeight] = useState(0);

  const [os, setOs] = useState('windows');
  const [configurationStyle, setConfigurationStyle] = useState('full');
  const [legendStyle, setLegendStyle] = useState('icon');

  const fontPromises = useRef({}); // in-flight fetches

  const layouts = [
    'ANSI',
    'ISO',
    'JIS',
    'ABNT',
    'Ergodox',
    'Ortho',
    'Split',
    'Other',
  ];

  const loadFont = useCallback(
    (cacheKey, apiUrl) =>
      fetchFont(cacheKey, apiUrl, fonts, fontPromises.current, setFonts),
    [fonts]
  ); // loads and caches a font once (fetchFont in utils/fontHelpers.js, one copy in four screens until chain 10, Oct 2026): the loaded fonts from the state, the fetches in flight from the ref

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });

        const [legendsResponse, layoutResponse] = await Promise.all([
          api.get(`/api/legends/${legendsId}`), // Fetch legends
          // A fixed production layout made to preview legend sets. The hard-coded ID stays: this is an admin-only screen, and a missing document is accepted (user decision, Oct 2026).
          api.get(`/api/layouts/6737b43ed45134fa8b77d2e9`),
        ]);

        const legendsData = legendsResponse.data;
        const layoutData = layoutResponse.data;

        const keycapData = assignLegendsToLayout(
          layoutData.layout,
          legendsData.legends,
          { configurationStyle, os, legendStyle },
          { defaultTextObject, defaultIconObject }
        ); // Assign legends to layout (utils/legendAssignment.js), each over the preview's default entry

        setName(legendsData.name);
        setSupportedLayouts(legendsData.supportedLayouts);
        setIsActive(legendsData.isActive);
        setLegends(legendsData.legends);

        setKeycapData(keycapData);
        setLayoutWidth(calculateLayoutWidth(layoutData.layout));
        setLayoutHeight(calculateLayoutHeight(layoutData.layout));

        dispatch({ type: 'FETCH_SUCCESS' });
      } catch (err) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(err),
        });
      }
    };

    fetchData(); // fetches the legends information based on the legends id, from the backend
  }, [legendsId]);

  useEffect(() => {
    if (keycapData.length > 0 && legends.length > 0) {
      const updatedKeycapData = assignLegendsToLayout(
        keycapData,
        legends,
        { configurationStyle, os, legendStyle },
        { defaultTextObject, defaultIconObject }
      );

      setKeycapData(updatedKeycapData); // Update the keycapData with the filtered legends
    }
  }, [legends, configurationStyle, os, legendStyle]); // whenever legends or filters change, the current legends are applied to the keycapData

  const submitHandler = async (e) => {
    e.preventDefault(); // used, so the page doesn't refresh, when the submit button is clicked
    try {
      dispatch({ type: 'UPDATE_REQUEST' });

      await api.put(`/api/legends/${legendsId}`, {
        _id: legendsId,
        name: name,
        supportedLayouts: supportedLayouts,
        isActive: isActive,
        legends: legends,
      });
      dispatch({
        type: 'UPDATE_SUCCESS',
      });
      toast.success('Legends gespeichert');
      navigate('/admin/legends');
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: 'UPDATE_FAIL' });
    }
  }; // a put request to the backend, that ultimalty updates the legends information based on the data from the Form

  const handleChange = (value) => {
    try {
      const newLegends = JSON.parse(value);
      setLegends(newLegends); // Update legends state
    } catch (error) {
      return null; // don't update the legends if the json is invalid, so that during editing no errors are thrown
    }
  };

  const toggleReadOnly = () => {
    setReadOnly(!readOnly);
  };

  const handleSupportedLayoutsChange = (e) => {
    setSupportedLayouts(
      addSelectedOptions(supportedLayouts, e.target.selectedOptions)
    ); // adds the selected options once each
  };

  const removeCompatibility = (item) => {
    setSupportedLayouts(supportedLayouts.filter((layout) => layout !== item));
  }; // removes an item from the supportedLayouts

  return (
    <Container>
      <Container>
        <title>Legends bearbeiten</title>

        <h1 className="screen-heading">Legends bearbeiten</h1>

        {loading || loadingUpdate ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox>
        ) : (
          <Form onSubmit={submitHandler}>
            <Form.Group className="mb-3" controlId="name">
              <Form.Label>Name</Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Form.Group>

            <Form className="mb-3">
              <Form.Group controlId="supportedLayouts">
                <Form.Label className="d-flex">Supported Layouts</Form.Label>
                <Form.Select
                  className="mobile-device-compatible"
                  onChange={handleSupportedLayoutsChange}
                  required
                >
                  {layouts.map((layout) => (
                    <option key={layout} value={layout}>
                      {layout}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <div className="selected-items">
                {supportedLayouts.map((item, index) => (
                  <div
                    key={index}
                    className="selected-item"
                    style={{ fontSize: '14px', marginLeft: '10px' }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="10"
                      height="10"
                      fill="currentColor"
                      className="bi bi-x-lg"
                      viewBox="0 0 16 16"
                      onClick={() => removeCompatibility(item)}
                      style={{ cursor: 'pointer' }}
                    >
                      <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z" />
                    </svg>{' '}
                    {item}
                  </div>
                ))}
              </div>
            </Form>

            <Form.Group className="mb-3" controlId="isActive">
              <Form.Label className="d-flex">
                Active
                <OverlayTrigger
                  trigger="click"
                  rootClose // close the popover when the user clicks outside of it
                  placement="right"
                  overlay={
                    <Popover>
                      <Popover.Body>
                        Bestimmt, ob dieses Legend-Set dem Kunden im Customizer
                        zur Verfügung steht.
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={isActive}
                onChange={(e) => setIsActive(e.target.value)}
                required
              >
                <option className="option-tag" value={true}>
                  Yes
                </option>
                <option className="option-tag" value={false}>
                  No
                </option>
              </Form.Select>
            </Form.Group>

            <Form.Group
              className="mb-3 d-flex justify-content-between align-center"
              controlId="legends"
            >
              <Form.Label>Legends</Form.Label>
              <Button
                style={{ fontSize: '12px' }}
                className="no-css"
                variant="none"
                onClick={toggleReadOnly}
              >
                {readOnly ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="currentColor"
                    className="bi bi-pencil-square"
                    viewBox="0 0 16 16"
                  >
                    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                    <path
                      fillRule="evenodd"
                      d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="red"
                    className="bi bi-x-square"
                    viewBox="0 0 16 16"
                  >
                    <path d="M14 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z" />
                    <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708" />
                  </svg>
                )}
              </Button>
            </Form.Group>

            <div className="editor-container">
              <div className="ide-wrapper">
                <Editor
                  height="100%"
                  defaultLanguage="json"
                  value={JSON.stringify(legends, null, 2)}
                  onChange={handleChange}
                  options={{
                    readOnly: readOnly,
                    minimap: { enabled: true },
                    lineNumbers: 'on',
                    automaticLayout: true,
                    fontSize: 14,
                    wordWrap: 'on',
                    scrollBeyondLastLine: false,
                    quickSuggestions: true,
                    tabSize: 2,
                    bracketPairColorization: true,
                    renderWhitespace: false,
                    mouseWheelZoom: true,
                    smoothScrolling: true,
                    cursorBlinking: 'smooth',
                    cursorStyle: 'line',
                  }}
                />
              </div>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3" controlId="os">
                    <Form.Label style={{ fontSize: '10px' }}>
                      Operating System
                    </Form.Label>
                    <Form.Select
                      value={os}
                      onChange={(e) => setOs(e.target.value)}
                    >
                      <option value="windows">Windows</option>
                      <option value="mac">macOS</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3" controlId="configurationStyle">
                    <Form.Label style={{ fontSize: '10px' }}>
                      Configuration Style
                    </Form.Label>
                    <Form.Select
                      value={configurationStyle}
                      onChange={(e) => setConfigurationStyle(e.target.value)}
                    >
                      <option value="full">Full</option>
                      <option value="simple">Simple</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3" controlId="legendStyle">
                    <Form.Label style={{ fontSize: '10px' }}>
                      Legend Style
                    </Form.Label>
                    <Form.Select
                      value={legendStyle}
                      onChange={(e) => setLegendStyle(e.target.value)}
                    >
                      <option value="text">Text</option>
                      <option value="icon">Icon</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mt-4">
                <Col md={12}>
                  <div
                    className="d-flex justify-content-center"
                    style={{
                      position: 'relative',
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg" // Ensure correct namespace
                      viewBox={`0 0 ${layoutWidth} ${layoutHeight}`}
                      style={{
                        maxWidth: layoutWidth * 3,
                        width: '100%',
                        height: 'auto',
                        overflow: 'visible', // To prevent clipping (is needed to ensure that the svg is not being clipped at the bottom and right side due to rendering nuances like rounding errors and anti-aliasing)
                      }}
                      version="1.1"
                      id="legends"
                    >
                      {
                        <KeycapBaseLayer
                          keycapData={keycapData}
                          totalVerticalSpacing={totalVerticalSpacing}
                          dataComingFromLayoutEditor={false}
                          dataComingFromColorPicker={false}
                          visibleGroup={null}
                          hexColorCode={null}
                          dataComingFromLegendsEditor={true}
                        />
                      }

                      {
                        (totalVerticalSpacing = 0) /* Reset totalVerticalSpacing to 0, becaus it will be reused in the next keycapData.map function */
                      }

                      {
                        <KeycapOverlay
                          keycapData={keycapData}
                          settings={null}
                          selectedTextIndex={null}
                          selectedIconIndex={null}
                          totalVerticalSpacing={totalVerticalSpacing}
                          handleKeycapClick={null}
                          dataComingFromLayoutEditor={false}
                          dataComingFromLegendsEditor={true}
                          fonts={fonts}
                          setFonts={setFonts}
                          loadFont={loadFont}
                        />
                      }

                      {
                        <StaticKeycapShadows
                          keycapData={keycapData}
                          triggerKeycapShadowRerender={null}
                          handleKeycapClick={null}
                          dataComingFromLayoutEditor={false}
                          dataComingFromLegendsEditor={true}
                        />
                      }
                    </svg>
                  </div>
                </Col>
              </Row>
            </div>

            <div className="d-flex justify-content-center align-items-center mt-5">
              <Button
                variant="secondary"
                className="mx-3"
                disabled={loadingUpdate}
                type="submit"
              >
                {/* disabled prop is set to loadingUpdate, because it should not be possible, to click the update button multiple times */}
                Speichern
              </Button>
            </div>
          </Form>
        )}
      </Container>
    </Container>
  );
}
